import UndoIcon from "@material-ui/icons/Undo";
import { Button } from "components";

import { formatCurrency, parseCurrencyValue } from "../pricingFormat";

const DIAS_NA_TELA = 3;
const ROTULO = { manter: "Manter", baixar: "Baixar", subir: "Subir" };
const VERBO = { manter: "manter", baixar: "baixar", subir: "subir" };

// Mesmas cores dos selos da tela.
const titulo = cor => ({
  margin: 0,
  fontSize: 12,
  fontWeight: 700,
  letterSpacing: "0.04em",
  textTransform: "uppercase",
  color: cor
});
const nota = { margin: 0, fontSize: 12, color: "#757575" };
const th = {
  background: "#f7faf0",
  color: "#757575",
  fontSize: 12,
  fontWeight: 500,
  padding: 8,
  borderBottom: "1px solid #e0e0e0",
  textAlign: "left"
};
const td = {
  padding: 8,
  borderBottom: "1px solid #eeeeee",
  fontSize: 13,
  fontVariantNumeric: "tabular-nums"
};

const numero = valor =>
  valor == null
    ? "--"
    : Number(valor).toLocaleString("pt-BR", { maximumFractionDigits: 1 });

const dataCurta = dia => {
  const [, mes, d] = String(dia).split("-");
  return d && mes ? `${d}/${mes}` : String(dia);
};

const praticadoTexto = item => {
  if (item.praticado == null) return "--";
  const ultima = (item.sugestoes || []).slice(-1)[0];
  const sugerido = parseCurrencyValue(ultima?.preco);
  const diferente =
    sugerido && Math.abs(item.praticado - sugerido) / sugerido > 0.01;
  return `${formatCurrency(item.praticado)}${
    diferente ? " (diferente da sugestão)" : ""
  }`;
};

const WhyPanel = ({ v5, editado, onDesfazer, onFechar }) => {
  const numeros = v5.numeros_usados || [];
  const memoria = (v5.memoria || []).slice(0, DIAS_NA_TELA);

  return (
    <div
      style={{
        // Fica na área visível mesmo com a tabela rolada para o lado.
        position: "sticky",
        left: 0,
        boxSizing: "border-box",
        maxWidth: "min(1480px, calc(100vw - 140px))",
        padding: "20px 24px",
        background: "#f3f8fd",
        display: "flex",
        flexDirection: "column",
        gap: 16,
        whiteSpace: "normal",
        textAlign: "left"
      }}
    >
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "minmax(0, 1.3fr) minmax(0, 1.45fr)",
          gap: 24
        }}
      >
        <section
          aria-label="Por que a V5"
          style={{ display: "flex", flexDirection: "column", gap: 10 }}
        >
          <h3 style={titulo("#1976d2")}>
            Por que a V5 recomenda {VERBO[v5.recomendacao] || ""}
          </h3>
          {v5.referencia ? (
            <p style={{ margin: 0, fontSize: 12.5, color: "#757575" }}>
              {v5.preco_referencia_fonte === "gold"
                ? `O catálogo não estava disponível: partiu da média vendida, ${v5.referencia}, que pode estar defasada.`
                : `Partiu de ${v5.referencia}, o preço do catálogo convertido em média.`}
            </p>
          ) : null}
          <p style={{ margin: 0, fontSize: 14, lineHeight: 1.6 }}>
            {v5.justificativa}
          </p>
          {numeros.length ? (
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
                gap: 8
              }}
            >
              {numeros.map(item => (
                <div
                  key={item.chave}
                  style={{
                    background: item.destaque ? "#fff8e1" : "#fff",
                    border: `1px solid ${
                      item.destaque ? "#ffcc80" : "#e0e0e0"
                    }`,
                    borderRadius: 8,
                    padding: "8px 10px"
                  }}
                >
                  <div
                    style={{
                      fontSize: 16,
                      fontWeight: 700,
                      fontVariantNumeric: "tabular-nums"
                    }}
                  >
                    {item.valor}
                  </div>
                  <div style={{ fontSize: 12, color: "#757575" }}>
                    {item.rotulo}
                  </div>
                </div>
              ))}
            </div>
          ) : null}
          {v5.contra_sinal_do_dia && numeros.some(n => n.destaque) ? (
            <p style={nota}>
              Em amarelo: os números que explicam por que a recomendação vai
              contra o sinal de hoje.
            </p>
          ) : null}
          {(v5.alertas || []).includes("mudanca_grande_sem_evidencia") ? (
            <p style={{ ...nota, color: "#e65100" }}>
              Mudança acima de 10% sem sobra até vencer, falta, mudança de CMC
              ou elasticidade medida que a sustente. Confira antes de aplicar.
            </p>
          ) : null}
        </section>

        <section
          aria-label="Últimos dias"
          style={{ display: "flex", flexDirection: "column", gap: 10 }}
        >
          <h3 style={titulo("#757575")}>Últimos dias</h3>
          {memoria.length ? (
            <>
              <table
                style={{
                  borderCollapse: "separate",
                  borderSpacing: 0,
                  width: "100%",
                  background: "#fff",
                  border: "1px solid #e0e0e0",
                  borderRadius: 8,
                  overflow: "hidden"
                }}
              >
                <thead>
                  <tr>
                    <th style={th}>Dia</th>
                    <th style={th}>Sugestão do dia</th>
                    <th style={th}>Praticado</th>
                    <th style={th}>Vendeu × previsto</th>
                    <th style={th}>Sobra</th>
                  </tr>
                </thead>
                <tbody>
                  {memoria.map(item => (
                    <tr key={item.dia}>
                      <td style={{ ...td, color: "#757575" }}>
                        {dataCurta(item.dia)}
                      </td>
                      <td style={td}>
                        {(item.sugestoes || [])
                          .map(
                            s =>
                              `${
                                ROTULO[s.recomendacao] || "--"
                              } ${formatCurrency(s.preco)} (${s.hora})`
                          )
                          .join(" → ")}
                      </td>
                      <td style={td}>{praticadoTexto(item)}</td>
                      <td style={td}>
                        {numero(item.vendido)} × {numero(item.previsto)}
                      </td>
                      <td style={td}>{numero(item.sobra)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <p style={nota}>
                Praticado é a média que de fato se vendeu no dia. Sobra é o
                estoque no início do dia seguinte.
              </p>
            </>
          ) : (
            <p style={nota}>Sem histórico dos últimos dias.</p>
          )}
        </section>
      </div>

      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 12,
          paddingTop: 14,
          borderTop: "1px solid #e0e0e0",
          flexWrap: "wrap"
        }}
      >
        <span style={{ fontSize: 14, color: "rgba(0, 0, 0, 0.75)" }}>
          V5 <b style={{ color: "#1976d2" }}>{v5.preco}</b> · {v5.selo}
        </span>
        <div
          style={{
            marginLeft: "auto",
            display: "flex",
            gap: 8,
            flexWrap: "wrap"
          }}
        >
          {editado ? (
            <Button
              variant="outlined"
              color="primary"
              startIcon={<UndoIcon />}
              onClick={onDesfazer}
            >
              Desfazer
            </Button>
          ) : null}
          <Button onClick={onFechar} style={{ color: "#757575" }}>
            Fechar
          </Button>
        </div>
      </div>
    </div>
  );
};

export default WhyPanel;
